import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { Button, Divider, Flex, Form, Typography } from "antd"
import React, { useRef } from "react"
import { useTranslation } from "react-i18next"

import { LeadViewQualificationQuestions } from "../../LeadViewPage/components/LeadViewQualificationQuestions.tsx"
import { type LeadQualificationQuestion } from "../../types/lead.types.ts"
import { WorkspaceCard } from "../cards/WorkspaceCard.tsx"
import { WorkspaceCardValue } from "../cards/WorkspaceCardValue.tsx"
import { type LeadPatchSender } from "../lead-workspace-patch.ts"
import { leadQualificationQuestions } from "./lead-qualification.ts"
import { TextInput } from "../../../../components/Input/TextInput.tsx"

const { Text } = Typography

interface QualificationValues {
  qualification_questions: LeadQualificationQuestion[]
}

interface QualificationEditorProps {
  questions: LeadQualificationQuestion[]
  saving: boolean
  onChange: (questions: LeadQualificationQuestion[]) => void
}

/**
 * Mirrors the Lead Checklist's own edit form (`LeadViewChecklistEdit`) —
 * `Form.List` add/remove and a danger delete icon per row. (The wizard's
 * fixed, positional checklist component this replaced lost its last
 * renderer and was deleted; the default question set now lives in
 * `lead-qualification.ts`.)
 *
 * Only a *newly added* row gets an editable question-text field — an
 * existing question keeps reading as a label, exactly as it always has (the
 * request only ever asks to answer, add or delete questions, never to rename
 * one), which also keeps the single-question "getByRole('textbox')" test
 * passing. Which rows count as "existing" is captured once, by `field.key`,
 * on the very first render — `field.key` stays stable for a row across
 * later adds/removes even as its numeric `field.name` (and therefore array
 * index) shifts, which a plain index comparison would get wrong the moment
 * an earlier row is deleted.
 */
function QualificationEditor({
  questions,
  saving,
  onChange
}: QualificationEditorProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<QualificationValues>()
  const originalQuestionByKey = useRef<Map<number, string> | null>(null)

  return (
    <Form
      form={form}
      disabled={saving}
      initialValues={{ qualification_questions: questions }}
      onValuesChange={(_, values) =>
        onChange(
          (values?.qualification_questions ?? []).map((q) => ({
            question: q?.question ?? "",
            answer: q?.answer?.trim() || ""
          }))
        )
      }
    >
      <Form.List name={"qualification_questions"}>
        {(fields, { add, remove }) => {
          if (!originalQuestionByKey.current) {
            originalQuestionByKey.current = new Map(
              fields.map((field, index) => [
                field.key,
                questions[index]?.question ?? ""
              ])
            )
          }

          return (
            <Flex vertical gap={"small"}>
              {fields.map((field, index) => {
                const originalQuestion = originalQuestionByKey.current!.get(
                  field.key
                )
                const isNew = originalQuestion === undefined

                return (
                  <div key={field.key}>
                    {index > 0 && <Divider className={"my-2"} />}
                    <Flex gap={"middle"} align={"start"}>
                      {isNew ? (
                        <Form.Item<
                          QualificationValues["qualification_questions"]
                        >
                          className={"m-0 min-w-0 flex-1"}
                          name={[field.name, "question"]}
                          rules={[
                            {
                              required: true,
                              whitespace: true,
                              message: t(
                                "leadWorkspace.cards.qualification.questionRequired"
                              )
                            }
                          ]}
                        >
                          <TextInput
                            placeholder={t(
                              "leadWorkspace.cards.qualification.questionPlaceholder"
                            )}
                          />
                        </Form.Item>
                      ) : (
                        <>
                          <Form.Item<
                            QualificationValues["qualification_questions"]
                          >
                            hidden
                            name={[field.name, "question"]}
                          >
                            <input />
                          </Form.Item>
                          <Text className={"min-w-0 flex-1 pt-1"}>
                            {originalQuestion}
                          </Text>
                        </>
                      )}
                      <Form.Item<
                        QualificationValues["qualification_questions"]
                      >
                        className={"m-0 w-64 max-w-full shrink-0"}
                        name={[field.name, "answer"]}
                      >
                        <TextInput
                          placeholder={t(
                            "leadWorkspace.cards.qualification.placeholder"
                          )}
                        />
                      </Form.Item>
                      <Button
                        type={"text"}
                        danger
                        className={"shrink-0"}
                        icon={<DeleteOutlined />}
                        aria-label={t(
                          "leadWorkspace.cards.qualification.remove"
                        )}
                        onClick={() => remove(field.name)}
                      />
                    </Flex>
                  </div>
                )
              })}
              <Button
                type={"dashed"}
                block
                icon={<PlusOutlined />}
                onClick={() => add({ question: "", answer: "" })}
              >
                {t("leadWorkspace.cards.qualification.add")}
              </Button>
            </Flex>
          )
        }}
      </Form.List>
    </Form>
  )
}

interface LeadQualificationCardProps {
  questions?: LeadQualificationQuestion[] | null
  canEdit?: boolean
  onPatch: LeadPatchSender
}

function LeadQualificationCard({
  questions,
  canEdit,
  onPatch
}: LeadQualificationCardProps) {
  const { t } = useTranslation()
  const asked = leadQualificationQuestions(questions)

  return (
    <WorkspaceCard<QualificationValues>
      cardId={"qualification"}
      title={t("leadWorkspace.cards.qualification.title")}
      canEdit={canEdit}
      values={{ qualification_questions: asked }}
      onSave={onPatch}
      validate={({ qualification_questions }) =>
        qualification_questions.every((q) => !!q.question?.trim())
      }
      renderEdit={({ values, setValues, saving }) => (
        <QualificationEditor
          questions={values.qualification_questions}
          saving={saving}
          onChange={(next) => setValues({ qualification_questions: next })}
        />
      )}
    >
      {({ startEdit }) => (
        <Flex vertical gap={"middle"}>
          <Text type={"secondary"} className={"text-sm"}>
            {t("leadWorkspace.cards.qualification.description")}
          </Text>

          {/* Every configured question is always shown — an unanswered one
              reads as an empty field, not as a reason to hide the whole list.
              An explicitly emptied list (every question deleted) is genuinely
              empty, distinct from a lead that never got any configured. */}
          {asked.length ? (
            <LeadViewQualificationQuestions qualificationQuestions={asked} />
          ) : (
            <WorkspaceCardValue
              placeholder={t("leadWorkspace.cards.qualification.empty")}
            />
          )}

          {/* Unlike the Checklist's own quick-add (removed 2026-08-30 as
              redundant with its Edit icon), qualification questions are
              answered/added/deleted as their own concept — this shortcut
              into the same Edit form is the obvious way to add the first
              question when the list is empty, and stays available once it
              isn't. */}
          {canEdit && (
            <Button
              type={"link"}
              className={"h-auto self-start p-0"}
              icon={<PlusOutlined />}
              onClick={startEdit}
            >
              {t("leadWorkspace.cards.qualification.addQuick")}
            </Button>
          )}
        </Flex>
      )}
    </WorkspaceCard>
  )
}

export { type QualificationValues, LeadQualificationCard }
